import axios from 'axios'
import * as HttpStatus from 'http-status-codes'
import { each, get } from 'lodash'
import { Identifier } from 'react-admin'

import {
  captureExceptionIfProduction,
  sanitizeNonNullableStringFields,
  stripStopOffsets,
} from 'helpers/utils'

type TransitionProps = {
  id: string
  transitionAction: any
  cancelReason?: string | null
  cancelReasonNote?: string | null
  abortReason?: string | null
  abortCancelDetail?: string | null
  bidAmount?: string | null
  bidNote?: string | null
  recoveryReason?: string | null
}

export function transition({
  id,
  transitionAction,
  cancelReason = null,
  cancelReasonNote = null,
  abortReason = null,
  abortCancelDetail = null,
  bidAmount = null,
  bidNote = null,
  recoveryReason = null,
}: TransitionProps) {
  let url = `/api/v1/console/jobs/${id}/transition/`
  let payload: any = { transition: transitionAction }
  if (cancelReason !== null) {
    payload.cancel_reason = cancelReason
  }
  if (cancelReasonNote !== null) {
    payload.cancel_reason_note = cancelReasonNote
  }
  if (abortReason !== null) {
    payload.abort_reason = abortReason
  }
  if (abortCancelDetail !== null) {
    payload.abort_cancel_detail = abortCancelDetail
  }
  if (bidAmount !== null) {
    payload.bid_amount = bidAmount ? Number(String(bidAmount).replace(/,/gi, '')) : Number(bidAmount)
  }
  if (bidNote !== null) {
    payload.bid_note = bidNote
  }
  if (recoveryReason !== null) {
    payload.recovery_reason = recoveryReason
  }
  return axios.post(url, payload).then((response) => {
    if (response.status === HttpStatus.OK) {
      return response.data
    }
  })
}

export function issueTransition(id: Identifier, payload: any, issueClass: string) {
  let url = ''
  if (issueClass === 'JOB') {
    url = `/api/v1/console/issues/${id}/transition/`
  } else if (issueClass === 'LOAD') {
    url = `/api/v1/console/load-issues/${id}/transition/`
  } else if (issueClass === 'GENERIC') {
    url = `/api/v1/console/generic-issues/${id}/transition/`
  } else {
    throw new Error('Invalid issue class')
  }
  return axios.post(url, payload).then((response) => {
    if (response.status === HttpStatus.OK) {
      return response.data
    }
  })
}

export function reportIssue(id: Identifier, payload: any) {
  let url = `/api/v1/console/jobs/${id}/report/`
  return axios.post(url, payload).then((response) => {
    if (response.status === HttpStatus.OK) {
      return response.data
    }
  })
}

export function editIssue(id: Identifier, payload: any) {
  let url = `/api/v1/console/issues/${id}/`
  return axios.patch(url, payload).then((response) => {
    if (response.status === HttpStatus.OK) {
      return response.data
    }
  })
}

export function editAssignedDriverOrTruck(id: Identifier, payload: any) {
  let url = `/api/v1/console/jobs/${id}/assignment-change/`
  return axios.post(url, payload).then((response) => {
    if (response.status === HttpStatus.OK) {
      return response.data
    }
  })
}

export function getStatement(id?: string) {
  let url = `/api/v1/console/statements/${id}/`
  return axios.get(url).then((response) => {
    if (response.status === HttpStatus.OK) {
      return response.data
    }
  })
}

export function finalizeStatement(id: Identifier) {
  let url = `/api/v1/console/statements/${id}/finalize/`
  return axios.post(url).then((response) => {
    if (response.status === HttpStatus.OK) {
      return response.data
    }
  })
}

export async function exportStatements(email: string, months: any[][], date_type: string) {
  const url = '/api/v1/console/statements/csv-email-export/'
  const response = await axios.post(url, { email, months, date_type })
  return response.status === HttpStatus.OK
}

export async function exportTeamStatements(teamId: Identifier, dateStart: any, dateEnd: any) {
  // note: dateEnd is exclusive
  // quirk of this driver intelligence endpoint is that it expects datetimes to be ...
  //   - localized in NY timezone, and
  //   - formatted as follows: 1st of the month, at 0 hour, 0 min, 0 second
  const url = '/api/v1/driver-intelligence/monthly-statement/'
  const response = await axios.post(url, { team_id: teamId, date_start: dateStart, date_end: dateEnd })
  return response.status === HttpStatus.OK
}

export function upload(id: Identifier, formData: any) {
  let url = `/api/v1/console/jobs/${id}/upload/`
  return axios
    .post(url, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
    .then((response) => {
      if (response.status === HttpStatus.CREATED) {
        return response.data
      }
    })
}

export function parseRateConfirmation(file: any) {
  let formData = new FormData()
  formData.append('rate_confirmation_file', file)
  let url = '/api/v3/parse-rate-confirmation/'
  return axios.post(url, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
}

export function createExternalLoad(payload: any) {
  let url = '/api/v1/console/jobs/external/'
  return axios.post(url, payload)
}

export async function checkJobFeasibility(id: Identifier, payload: any) {
  let url = `/api/v1/console/jobs/${id}/check-feasibility/`
  return axios.post(url, payload).then((response) => {
    if (response.status === HttpStatus.OK) {
      return response.data
    }
  })
}

export async function checkLoadFeasibility(id: Identifier, payload: any) {
  return axios.post(`/api/v1/console/loads/${id}/check-feasibility/`, payload).then((response) => {
    if (response.status === HttpStatus.OK) {
      return response.data
    }
  })
}

export function reviewForApproval(id: Identifier) {
  return axios.get(`/api/v1/console/jobs/${id}/review-approval/`)
}

export function approveV2(id: Identifier, payload: any) {
  let url = `/api/v1/console/jobs/${id}/approve-v2/`
  return axios.post(url, payload)
}

export function approve(id: Identifier) {
  let url = `/api/v1/console/jobs/${id}/approve/`
  return axios.post(url).then((response) => {
    if (response.status === HttpStatus.OK) {
      return response.data
    }
  })
}

export function updateAndApprove(id: Identifier, payload: any) {
  let url = `/api/v1/console/jobs/${id}/update-and-approve/`
  return axios.post(url, payload).then((response) => {
    if (response.status === HttpStatus.OK) {
      return response.data
    }
  })
}

export function generateInvoice(id: Identifier, formValues: any, should_verify_delivery = false) {
  let url = `/api/v1/console/jobs/${id}/invoice/`
  if (should_verify_delivery) {
    url = `${url}?verify_delivery=true`
  }
  return axios.post(url, formValues).then((response) => {
    if (response.status === HttpStatus.CREATED) {
      return response.data
    }
  })
}

export function getTruckercloudRouteProgress(jobId: Identifier) {
  let url = `/api/v1/console/jobs/${jobId}/tc-progress/`
  return axios.get(url).then((response) => {
    if (response.status === HttpStatus.OK) {
      return response.data
    }
  })
}

export function reprocessTruckercloud(jobId: Identifier) {
  let url = `/api/v1/console/jobs/${jobId}/reprocess-truckercloud/`
  return axios.post(url).then((response) => {
    if (response.status === HttpStatus.OK) {
      return response.data
    }
  })
}

export function partialUpdateJob(jobId: Identifier, payload: any) {
  let url = `/api/v1/console/jobs/${jobId}/`
  return axios.patch(url, payload).then((response) => {
    if (response.status === HttpStatus.OK) {
      return response.data
    }
  })
}

export function partialUpdateLoad(loadId: Identifier, payload: any) {
  if (get(payload, 'stops')) {
    throw new Error('This method does not support updating load stops (call modifyLoadStops explicitly)')
  }

  sanitizeNonNullableStringFields(payload, ['comment', 'company_name', 'contact_name', 'contact_phone'])
  const url = `/api/v1/console/loads/${loadId}/`
  return axios.patch(url, payload).then((response) => {
    if (response.status === HttpStatus.OK) {
      return response.data
    }
  })
}

export function modifyLoadStops(loadId: Identifier, stops: any) {
  const url = `/api/v1/console/loads/${loadId}/modify-stops/`

  // clean fields before passing to backend
  each(stops, (stop) => {
    sanitizeNonNullableStringFields(stop, [
      'location_name',
      'location_address1',
      'location_address2',
      'location_zip',
      'notes',
    ])
    stripStopOffsets(stop)
  })

  return axios.post(url, stops).then((response) => {
    if (response.status === HttpStatus.CREATED) {
      return response.data
    }
  })
}

export function getJobBids(jobId: Identifier) {
  let url = `/api/v1/console/jobs/${jobId}/bids/`
  return axios.get(url).then((response) => {
    if (response.status === HttpStatus.OK) {
      return response.data
    }
  })
}

export function placeJobBid(jobId: Identifier, bidAmount: string, bidNotes: string) {
  let url = `/api/v1/console/jobs/${jobId}/bid/`
  const payload = {
    amount: bidAmount,
    notes: bidNotes,
  }
  return axios.post(url, payload).then((response) => {
    if (response.status === HttpStatus.CREATED) {
      return response.data
    }
  })
}

export async function addOrEditTrailerNumber(id: Identifier, payload: any) {
  return axios.post(`/api/v1/console/loads/${id}/ae-trailer-number/`, payload).then((response) => {
    if (response.status === HttpStatus.OK) {
      return response.data
    }
  })
}

// Get Jobs that contain loads that have the specified source_human_id or date/origin/destination.
export async function getDuplicateJobs(payload: any) {
  return axios
    .post('/api/v1/console/jobs/check-duplicate-jobs/', payload)
    .then((response) => {
      if (response.status === HttpStatus.OK) {
        return response.data
      }
    })
    .catch((error) => {
      captureExceptionIfProduction(error)
    })
}
